import React from "react";
import { getBranches, deleteBranch, reactivateBranch } from "../../../api/branches";
import {
  formatDocument,
  formatDate,
  setCookieByName
} from "../../../utils";
import { 
  Page, 
  Card, 
  Typography as Text, 
  Button, 
  Input as LoadingInput, 
  SweetAlert, 
  Toggle, 
  Dropdown 
} from "../../../design-system";
import Row from "../../../_components/Layout/Row";
import Column from "../../../_components/Layout/Column";
import { useUpdateCookies } from "./hooks";

export default () => {
  const ref = React.useRef();

  // Estados do componente
  const [loading, setLoading] = React.useState(true);
  const [searchValue, setSearchValue] = React.useState('');
  const [allBranches, setAllBranches] = React.useState([]);
  const [filteredBranches, setFilteredBranches] = React.useState([]);
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [selectedBranch, setSelectedBranch] = React.useState(null);

  const updateCookies = useUpdateCookies();

  const [showInactive, setShowInactive] = React.useState(false);
  const [showReactivateModal, setShowReactivateModal] = React.useState(false);

  /**
   * Carrega os dados das filiais na montagem do componente
   */
  React.useEffect(() => {
    getBranches()
      .then((response) => {
        const activeBranches = response.filter(branch => branch.status !== 2)
        setAllBranches(response);
        setCookieByName("branch_count", activeBranches.length);
      })
      .finally(() => setLoading(false));
  }, []);

  /**
   * Lógica de filtragem de uma filial com base no valor de busca
   */
  const filterBranch = React.useCallback((branch, value) => {
    const lowerValue = value.toLowerCase();

    if (branch.company) {
      return (
        branch.company.cnpj.includes(value) ||
        branch.company.fancy_name.toLowerCase().includes(lowerValue)
      );
    }

    return (
      branch.individual.cpf.includes(value) ||
      branch.name.toLowerCase().includes(lowerValue)
    );
  }, []);

  React.useEffect(() => {
    const baseList = allBranches.filter(branch => showInactive ? branch.status === 2 : branch.status !== 2);
    if (!searchValue) {
      setFilteredBranches(baseList);
      return;
    }
    const filtered = baseList.filter(branch => filterBranch(branch, searchValue));
    setFilteredBranches(filtered);
  }, [allBranches, showInactive, searchValue, filterBranch]);

  const handleSearch = (event) => {
    setSearchValue(event.target.value);
  };

  /**
   * Função para desativar uma filial
   */
  const handleDeleteBranch = () => {
    if (selectedBranch) {
      deleteBranch(selectedBranch.id)
        .then(() => {
          setAllBranches(prev => prev.map(b => b.id === selectedBranch.id ? { ...b, status: 2 } : b));
          setShowCancelModal(false);
          setSelectedBranch(null);

          getBranches()
            .then((response) => {
              const activeBranches = response.filter(branch => branch.status !== 2)
              setCookieByName("branch_count", activeBranches.length);
            })
        })
        .catch((error) => {
          console.error("Erro ao deletar filial:", error);
        });
    }
  };

  /**
   * Função para reativar uma filial
   */
  const handleReactivateBranch = () => {
    if (selectedBranch) {
      reactivateBranch(selectedBranch.id)
        .then(() => {
          setAllBranches(prev => prev.map(b => b.id === selectedBranch.id ? { ...b, status: 1 } : b));
          setShowReactivateModal(false);
          setSelectedBranch(null);

          getBranches()
            .then((response) => {
              const activeBranches = response.filter(branch => branch.status !== 2)
              setCookieByName("branch_count", activeBranches.length);
            })
        })
        .catch((error) => {
          console.error("Erro ao reativar filial:", error);
        });
    }
  };

  const handleOpenReactivateModal = (branch) => {
    setSelectedBranch(branch);
    setShowReactivateModal(true);
  };

  const handleOpenDeleteModal = (branch) => {
    setSelectedBranch(branch);
    setShowCancelModal(true);
  };

  /**
   * Renderiza o conteúdo principal da página
   */
  return (
    <Page
      title="Empresas cadastradas"
      headerAction={
        <Toggle 
          label="Mostrar filiais desativadas" 
          checked={showInactive} 
          onChange={(e) => setShowInactive(e.target.checked)} 
        />
      }
      loading={loading}
      ref={ref}
    >

      {allBranches.length > 10 && (
        <Row style={{ marginBottom: 20 }}>
          <Column xs="12" md="4" lg="4">
            <LoadingInput
              placeholder="Buscar filial (cnpj/cpf, razão social)"
              value={searchValue}
              onChange={handleSearch}
              icon={<i className="fa fa-search" />}
            />
          </Column>
        </Row>
      )}

      {showCancelModal && (
        <SweetAlert
          variant="info"
          showCancel={true}
          confirmBtnText="Desativar"
          cancelBtnText="Cancelar"
          title="Deseja desativar esta filial?"
          onConfirm={handleDeleteBranch}
          onCancel={() => setShowCancelModal(false)}
        />
      )}

      {showReactivateModal && (
        <SweetAlert
          variant="info"
          showCancel={true}
          confirmBtnText="Reativar"
          cancelBtnText="Cancelar"
          title="Deseja reativar esta filial?"
          onConfirm={handleReactivateBranch}
          onCancel={() => setShowReactivateModal(false)}
        >
          A filial voltará a aparecer na lista de ativas
        </SweetAlert>
      )}

      <Row>
        {filteredBranches.map((branch) => {
          const name = branch.individual
            ? branch.name
            : branch.company.fancy_name;

          const cpfCnpj = branch.individual
            ? branch.individual.cpf
            : branch.company.cnpj;

          const rowStyle = { textAlign: 'left', marginBottom: '4px', fontSize: '14px' };
          const labelStyle = { fontWeight: 'bold', color: '#333' };
          const valueStyle = { fontWeight: 'normal', color: '#555' };

          const formatRow = (label, value, valueColor = null) => (
            <div style={rowStyle}>
              <span style={labelStyle}>{label}: </span>
              <span style={{ ...valueStyle, ...(valueColor ? { color: valueColor, fontWeight: 'bold' } : {}) }}>
                {value}
              </span>
            </div>
          );

          const branchPlan = branch.branch_plan
            ? formatRow('Assinatura', branch.branch_plan.plan_name)
            : formatRow('Assinatura', 'Não possui', 'red');

          const nextInvoice = branch.branch_plan && formatRow('Próxima cobrança', formatDate(branch.branch_plan.end_date));

          const digitalCertificate = branch.certificate
            ? formatRow('Certificado', `vence em ${formatDate(branch.certificate.expiry_date)}`)
            : formatRow('Certificado', 'não cadastrado', 'red');

          const remainingNotes = branch.branch_plan && formatRow(
            'Notas disponíveis',
            Math.max(0, branch.branch_plan.notes_quantity - branch.branch_plan.total_sended_notes)
          );

          return (
            <Column xs="12" md="4" lg="3" key={`branch-list-${branch.id}`}>
              <Card>
                <Card.Header title={name}>
                  <Dropdown>
                    <Dropdown.Toggle />
                    <Dropdown.Menu>
                      <Dropdown.Item 
                        icon="fas fa-edit text-primary" 
                        onClick={(e) => {
                          e.stopPropagation();
                          updateCookies(branch);
                          window.location.href = "/settings/branch";
                        }}
                      >
                        Editar Filial
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item 
                        icon="fas fa-id-card" 
                        onClick={(e) => {
                          e.stopPropagation();
                          updateCookies(branch);
                          window.location.href = "/digital-certificate";
                        }}
                      >
                        Certificado Digital
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item 
                        icon="fas fa-receipt" 
                        onClick={(e) => {
                          e.stopPropagation();
                          updateCookies(branch);
                          window.location.href = "/plans";
                        }}
                      >
                        Visualizar Assinatura
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item 
                        icon={branch.status === 2 ? "fas fa-check text-success" : "fas fa-trash text-danger"} 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (branch.status === 2) {
                            handleOpenReactivateModal(branch);
                          } else {
                            handleOpenDeleteModal(branch);
                          }
                        }}
                      >
                        {branch.status === 2 ? "Reativar Filial" : "Desativar Filial"}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Card.Header>
                <Card.Body>
                  <div style={{ padding: '0 8px', textAlign: 'left' }}>
                    {formatRow('CNPJ/CPF', formatDocument(cpfCnpj))}
                    {branchPlan}
                    {nextInvoice}
                    {remainingNotes}
                    <div style={{ height: '8px' }} />
                    {digitalCertificate}
                  </div>
                </Card.Body>
                <Card.Footer>
                  <Button variant="tertiary" onClick={() => updateCookies(branch)} size="sm">
                    Selecionar empresa
                  </Button>
                </Card.Footer>
              </Card>
            </Column>
          );
        })}
      </Row>
    </Page>
  );
};



// WEBPACK FOOTER //
// ./src/views/AppViews/Branches/index.jsx